import { safeFetch } from "../utils/safeFetch";
import { buildApiError } from "../utils/apiErrors";
import { claimTableSession } from "./TablesAPI";

const extractVariantIds = (p = {}) => {
    if (Array.isArray(p?.variantIds)) {
        return p.variantIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
    }

    if (Array.isArray(p?.orderProductVariants)) {
        return p.orderProductVariants
            .map((opv) => Number(opv?.variantId ?? opv?.variant?.id))
            .filter((id) => Number.isInteger(id) && id > 0);
    }

    const singleVariantId = Number(p?.variantId ?? p?.variant?.id);
    if (Number.isInteger(singleVariantId) && singleVariantId > 0) return [singleVariantId];
    return [];
};

const normalizeOrderProductsForPatch = (orderProducts = []) =>
    (Array.isArray(orderProducts) ? orderProducts : [])
        .map((p) => {
            const normalized = {
                productId: Number(p?.productId ?? p?.product?.id),
                variantIds: extractVariantIds(p),
                quantity: Number(p?.quantity) || 0,
                ...(p?.unitPrice !== undefined ? { unitPrice: Number(p.unitPrice) } : {}),
                ...(p?.discount !== undefined ? { discount: Number(p.discount) } : {}),
                ...(p?.note !== undefined ? { note: p.note } : {}),
            };
            const rowId = Number(p?.orderProductId ?? p?.id);
            if (Number.isInteger(rowId) && rowId > 0) normalized.orderProductId = rowId;
            return normalized;
        })
        .filter((p) => Number.isInteger(p.productId) && p.productId > 0 && p.quantity > 0);

const normalizeOrderPackagesForPatch = (orderPackages = []) =>
    (Array.isArray(orderPackages) ? orderPackages : [])
        .map((p) => {
            const normalized = {
                packageId: Number(p?.packageId ?? p?.package?.id),
                quantity: Number(p?.quantity) || 0,
                ...(p?.unitPrice !== undefined ? { unitPrice: Number(p.unitPrice) } : {}),
                ...(p?.discount !== undefined ? { discount: Number(p.discount) } : {}),
                ...(p?.note !== undefined ? { note: p.note } : {}),
            };
            const rowId = Number(p?.orderPackageId ?? p?.id);
            if (Number.isInteger(rowId) && rowId > 0) normalized.orderPackageId = rowId;
            return normalized;
        })
        .filter((p) => Number.isInteger(p.packageId) && p.packageId > 0 && p.quantity > 0);

const logHeliteFrontendPayload = (label, payload) => {
    if (!payload) return;

    const hasStatusRoomCharge = Boolean(payload.heliteCharge);
    const hasPaymentRoomCharge = Array.isArray(payload.payments)
        && payload.payments.some((payment) => String(payment?.method || "").toUpperCase() === "ROOM_CHARGE");

    if (!hasStatusRoomCharge && !hasPaymentRoomCharge) return;

    console.info(`[HElite frontend payload] ${label}`, payload);
};

const fetchCurrentOrder = async (orderId, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenant
        }
    });

    if (!response.ok) return null;
    return await response.json();
};

const normalizeOrderPatchPayload = async (orderId, updatedData, token, tenant) => {
    if (Array.isArray(updatedData)) {
        const orderProducts = normalizeOrderProductsForPatch(updatedData);
        return { orderProducts, orderPackages: [] };
    }

    const hasOrderProducts = Array.isArray(updatedData?.orderProducts);
    const hasOrderPackages = Array.isArray(updatedData?.orderPackages);

    if (hasOrderProducts && hasOrderPackages) {
        return {
            orderProducts: normalizeOrderProductsForPatch(updatedData.orderProducts),
            orderPackages: normalizeOrderPackagesForPatch(updatedData.orderPackages),
        };
    }

    const currentOrder = await fetchCurrentOrder(orderId, token, tenant);
    const orderProducts = hasOrderProducts
        ? normalizeOrderProductsForPatch(updatedData.orderProducts)
        : normalizeOrderProductsForPatch(currentOrder?.orderProducts || []);
    const orderPackages = hasOrderPackages
        ? normalizeOrderPackagesForPatch(updatedData.orderPackages)
        : normalizeOrderPackagesForPatch(currentOrder?.orderPackages || []);

    return { orderProducts, orderPackages };
};

const areSameVariantSets = (a = [], b = []) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const sortedA = [...a].map(Number).sort((x, y) => x - y);
    const sortedB = [...b].map(Number).sort((x, y) => x - y);
    return sortedA.every((v, i) => v === sortedB[i]);
};

const normalizeProductPatchPayload = async (orderId, updatedData, token, tenant) => {
    if (!updatedData || typeof updatedData !== "object") return updatedData;
    if (updatedData.orderPackageId !== undefined) {
        return { ...updatedData, orderPackageId: Number(updatedData.orderPackageId) };
    }
    if (updatedData.packageId !== undefined) return updatedData;
    if (Number.isInteger(Number(updatedData.orderProductId)) && Number(updatedData.orderProductId) > 0) {
        return { ...updatedData, orderProductId: Number(updatedData.orderProductId) };
    }

    const productId = Number(updatedData.productId);
    if (!Number.isInteger(productId) || productId <= 0) return updatedData;

    const currentOrder = await fetchCurrentOrder(orderId, token, tenant);
    const orderProducts = Array.isArray(currentOrder?.orderProducts) ? currentOrder.orderProducts : [];
    const requestedVariantIds = extractVariantIds(updatedData);
    const candidates = orderProducts.filter((p) => Number(p?.productId ?? p?.product?.id) === productId);
    const matched = requestedVariantIds.length > 0
        ? candidates.find((p) => areSameVariantSets(extractVariantIds(p), requestedVariantIds))
        : candidates[0];

    if (!matched?.id) return updatedData;

    const payload = { ...updatedData, orderProductId: Number(matched.id) };
    delete payload.productId;
    delete payload.variantId;
    delete payload.variantIds;
    return payload;
};

const normalizePackagePatchPayload = async (orderId, updatedData, token, tenant) => {
    if (!updatedData || typeof updatedData !== "object") return updatedData;

    const payload = {
        ...updatedData,
        ...(updatedData.quantity !== undefined ? { quantity: Number(updatedData.quantity) } : {}),
        ...(updatedData.unitPrice !== undefined ? { unitPrice: Number(updatedData.unitPrice) } : {}),
        ...(updatedData.discount !== undefined ? { discount: Number(updatedData.discount) } : {}),
    };

    if (Number.isInteger(Number(payload.orderPackageId)) && Number(payload.orderPackageId) > 0) {
        payload.orderPackageId = Number(payload.orderPackageId);
        return payload;
    }

    const packageId = Number(payload.packageId);
    if (!Number.isInteger(packageId) || packageId <= 0) return payload;

    const currentOrder = await fetchCurrentOrder(orderId, token, tenant);
    const currentPackages = Array.isArray(currentOrder?.orderPackages) ? currentOrder.orderPackages : [];
    const matched = currentPackages.find((p) => Number(p?.packageId ?? p?.package?.id) === packageId);
    if (!matched?.id) return payload;

    payload.orderPackageId = Number(matched.id);
    return payload;
};

export const getAllOrdini = async (token, tenant, options = {}) => {
    const noCache = Boolean(options?.noCache);
    const url = noCache ? `/api/orders?ts=${Date.now()}` : `/api/orders`;
    const response = await safeFetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenant
        }
    }, noCache ? null : 'allOrders');

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server o recuperare dati offline.");
        } else {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

const kitchenRequest = async (url, options, token, tenant) => {
    const response = await safeFetch(url, {
        ...options,
        headers: {
            ...(options?.body ? { "Content-Type": "application/json" } : {}),
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
            ...(options?.headers || {}),
        },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || `HTTP error! status: ${response.status}`);
    return data;
};

export const getKitchenPrintState = (tableId, token, tenant) => kitchenRequest(
    `/api/orders/kitchen-print-state/${tableId}`,
    { method: 'GET' },
    token,
    tenant
);

export const saveKitchenPrintState = (tableId, snapshot, token, tenant) => kitchenRequest(
    `/api/orders/kitchen-print-state/${tableId}`,
    { method: 'PUT', body: JSON.stringify({ snapshot }) },
    token,
    tenant
);

export const getKitchenMarches = (orderIds, token, tenant) => kitchenRequest(
    `/api/orders/kitchen-marches?orderIds=${encodeURIComponent((orderIds || []).join(','))}`,
    { method: 'GET' },
    token,
    tenant
);

export const recordKitchenMarches = (entries, token, tenant) => kitchenRequest(
    '/api/orders/kitchen-marches',
    { method: 'POST', body: JSON.stringify({ entries }) },
    token,
    tenant
);

export const getOrdineById = async (orderId, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenant
        }
    }, `order_${orderId}`);

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server o recuperare dati offline.");
        } else {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const creaComanda = async (comanda, token, tenant) => {
    if (comanda?.tableId != null) {
        await claimTableSession(comanda.tableId, token, tenant);
    }

    const response = await safeFetch(`/api/orders`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(comanda),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const aggiornaComanda = async (orderId, updatedData, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(updatedData),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            const errorText = await response.text().catch(() => "");
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const aggiornaRettificheComanda = async (orderId, adjustments, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}/adjustments`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(adjustments),
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
};

export const eliminaComanda = async (orderId, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    // Se 204 No Content, non c'è corpo da leggere
    if (response.status === 204) {
        return null;
    }

    return await response.json();
};

export const spostaProdottiComanda = async (orderId, updatedData, token, tenant) => {
    const payload = await normalizeOrderPatchPayload(orderId, updatedData, token, tenant);
    const response = await safeFetch(`/api/orders/${orderId}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const patchProdottoComanda = async (orderId, updatedData, token, tenant) => {
    const payload = await normalizeProductPatchPayload(orderId, updatedData, token, tenant);
    const response = await safeFetch(`/api/orders/${orderId}/patch`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            const errorText = await response.text().catch(() => "");
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const patchPacchettoComanda = async (orderId, updatedData, token, tenant) => {
    let payload = await normalizePackagePatchPayload(orderId, updatedData, token, tenant);
    const response = await safeFetch(`/api/orders/${orderId}/patch`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        const shouldRetryMissingRow =
            Number.isInteger(Number(payload?.orderPackageId)) &&
            (response.status === 400 || response.status === 404 || response.status === 500) &&
            /No record was found for an update/i.test(errorText);

        if (shouldRetryMissingRow) {
            const retriedPayload = await normalizePackagePatchPayload(orderId, { ...payload, orderPackageId: undefined }, token, tenant);
            if (Number.isInteger(Number(retriedPayload?.orderPackageId)) && Number(retriedPayload.orderPackageId) > 0) {
                const retryResponse = await safeFetch(`/api/orders/${orderId}/patch`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        'x-tenant-id': tenant,
                    },
                    body: JSON.stringify(retriedPayload),
                });

                if (retryResponse.ok) return await retryResponse.json();

                const retryErrorText = await retryResponse.text().catch(() => "");
                throw new Error(retryErrorText || `HTTP error! status: ${retryResponse.status}`);
            }
        }

        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const patchMenuPrezzoFissoComanda = async (orderId, updatedData, token, tenant) => {
    const response = await safeFetch(`/api/orders/${orderId}/patch`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify({
            ...updatedData,
            orderFixedPriceMenuId: Number(updatedData.orderFixedPriceMenuId),
            ...(updatedData.finalPrice !== undefined ? { finalPrice: Number(updatedData.finalPrice) } : {}),
        }),
    });

    if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
};

export const aggiornaStatusComanda = async (orderId, updatedStatus, token, tenant) => {
    logHeliteFrontendPayload(`PATCH /api/orders/${orderId}/status`, updatedStatus);

    const response = await safeFetch(`/api/orders/${orderId}/status`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(updatedStatus),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        } else {
            throw await buildApiError(response, `HTTP error! status: ${response.status}`);
        }
    }

    return await response.json();
};

export const partialCheckoutComanda = async (orderId, payload, token, tenant) => {
    logHeliteFrontendPayload(`POST /api/orders/${orderId}/partial-checkout`, payload);

    const response = await safeFetch(`/api/orders/${orderId}/partial-checkout`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            'x-tenant-id': tenant,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        if (response.status === 0) {
            throw new Error("Impossibile connettersi al server");
        }
        throw await buildApiError(response, `HTTP error! status: ${response.status}`);
    }

    return await response.json();
};
export const trasferisciProdotti = async ({ sourceTableId, destinationTableId, items }, token, tenant) => {
  const response = await safeFetch(`/api/orders/transfer-lines`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify({ sourceTableId, destinationTableId, items }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
};
